import { useCallback, useMemo } from "react"

import { normalizeTags } from "@/tags/values"

import { TRACKING_EVENTS, type TrackingEventName } from "@/lib/analytics"
import {
  metricValueFilterEquals,
  type MetricValueFilter,
} from "@/lib/metricValueFilters"

import type { FilterState, RunsSortDescriptor } from "../types/filterState"
import {
  countActiveRunsFilters,
  useTrackedRunsStringArrayActions,
} from "./useTrackedRunsStringArrayActions"

interface RunsFilterFieldSetter {
  <K extends keyof FilterState>(key: K, value: FilterState[K]): void
}

interface RunsFilterFieldUpdater {
  <K extends keyof FilterState>(
    key: K,
    updater: (prev: FilterState[K]) => FilterState[K],
  ): void
}

interface UseRunsFilterActionsParams {
  state: FilterState
  setField: RunsFilterFieldSetter
  updateField: RunsFilterFieldUpdater
  resetFilters: (sortDescriptor: RunsSortDescriptor) => void
  trackEvent: (
    event: TrackingEventName,
    properties?: Record<string, unknown>,
  ) => void
}

export function useRunsFilterActions({
  state,
  setField,
  updateField,
  resetFilters,
  trackEvent,
}: UseRunsFilterActionsParams) {
  const updateStatus = useCallback(
    (fn: (prev: string[]) => string[]) => updateField("statusFilters", fn),
    [updateField],
  )
  const updateTemplates = useCallback(
    (fn: (prev: string[]) => string[]) => updateField("templateFilters", fn),
    [updateField],
  )
  const updatePersonas = useCallback(
    (fn: (prev: string[]) => string[]) => updateField("personaFilters", fn),
    [updateField],
  )
  const updateAgents = useCallback(
    (fn: (prev: string[]) => string[]) => updateField("agentFilters", fn),
    [updateField],
  )
  const updateMetrics = useCallback(
    (fn: (prev: string[]) => string[]) => updateField("metricFilters", fn),
    [updateField],
  )
  const updateMetricValues = useCallback(
    (fn: (prev: MetricValueFilter[]) => MetricValueFilter[]) =>
      updateField("metricValueFilters", fn),
    [updateField],
  )
  const updateTags = useCallback(
    (fn: (prev: string[]) => string[]) =>
      updateField("tagFilters", (prev) => normalizeTags(fn(prev))),
    [updateField],
  )
  const updateMutations = useCallback(
    (fn: (prev: string[]) => string[]) => updateField("mutationFilters", fn),
    [updateField],
  )
  const updateTestSets = useCallback(
    (fn: (prev: string[]) => string[]) => updateField("testSetFilters", fn),
    [updateField],
  )
  const updateTestCases = useCallback(
    (fn: (prev: string[]) => string[]) => updateField("testCaseFilters", fn),
    [updateField],
  )
  const updateScheduledJobs = useCallback(
    (fn: (prev: string[]) => string[]) =>
      updateField("scheduledJobFilters", fn),
    [updateField],
  )

  const setMetadataFilters = useCallback(
    (filters: NonNullable<FilterState["metadataFilters"]>) =>
      setField("metadataFilters", filters),
    [setField],
  )

  const totalActiveFilters = countActiveRunsFilters(state)
  const hasActiveFilters = totalActiveFilters > 0

  const statusActions = useTrackedRunsStringArrayActions({
    values: state.statusFilters,
    update: updateStatus,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.STATUS_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.STATUS_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.STATUS_BULK_UPDATED,
    valuePropertyName: "status",
    countPropertyName: "total_status_filters",
    totalActiveFilters,
  })
  const templateActions = useTrackedRunsStringArrayActions({
    values: state.templateFilters,
    update: updateTemplates,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.TEMPLATE_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.TEMPLATE_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.TEMPLATE_BULK_UPDATED,
    valuePropertyName: "template",
    countPropertyName: "total_template_filters",
    totalActiveFilters,
  })
  const personaActions = useTrackedRunsStringArrayActions({
    values: state.personaFilters,
    update: updatePersonas,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.PERSONA_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.PERSONA_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.PERSONA_BULK_UPDATED,
    valuePropertyName: "persona",
    countPropertyName: "total_persona_filters",
    totalActiveFilters,
  })
  const agentActions = useTrackedRunsStringArrayActions({
    values: state.agentFilters,
    update: updateAgents,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.AGENT_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.AGENT_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.AGENT_BULK_UPDATED,
    valuePropertyName: "agent",
    countPropertyName: "total_agent_filters",
    totalActiveFilters,
  })
  const metricActions = useTrackedRunsStringArrayActions({
    values: state.metricFilters,
    update: updateMetrics,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.METRIC_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.METRIC_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.METRIC_BULK_UPDATED,
    valuePropertyName: "metric",
    countPropertyName: "total_metric_filters",
    totalActiveFilters,
  })
  const tagActions = useTrackedRunsStringArrayActions({
    values: state.tagFilters,
    update: updateTags,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.TAG_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.TAG_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.TAGS_UPDATED,
    valuePropertyName: "tag",
    countPropertyName: "total_tag_filters",
    totalActiveFilters,
  })
  const mutationActions = useTrackedRunsStringArrayActions({
    values: state.mutationFilters,
    update: updateMutations,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.MUTATION_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.MUTATION_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.MUTATION_BULK_UPDATED,
    valuePropertyName: "mutation",
    countPropertyName: "total_mutation_filters",
    totalActiveFilters,
  })
  const testSetActions = useTrackedRunsStringArrayActions({
    values: state.testSetFilters,
    update: updateTestSets,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.TEST_SET_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.TEST_SET_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.TEST_SET_BULK_UPDATED,
    valuePropertyName: "testSet",
    countPropertyName: "total_test_set_filters",
    totalActiveFilters,
  })
  const testCaseActions = useTrackedRunsStringArrayActions({
    values: state.testCaseFilters,
    update: updateTestCases,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.TEST_CASE_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.TEST_CASE_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.TEST_CASE_BULK_UPDATED,
    valuePropertyName: "testCase",
    countPropertyName: "total_test_case_filters",
    totalActiveFilters,
  })
  const scheduledJobActions = useTrackedRunsStringArrayActions({
    values: state.scheduledJobFilters,
    update: updateScheduledJobs,
    trackEvent,
    addedEvent: TRACKING_EVENTS.RUNS_FILTER.SCHEDULED_JOB_ADDED,
    removedEvent: TRACKING_EVENTS.RUNS_FILTER.SCHEDULED_JOB_REMOVED,
    bulkUpdatedEvent: TRACKING_EVENTS.RUNS_FILTER.SCHEDULED_JOB_BULK_UPDATED,
    valuePropertyName: "jobId",
    countPropertyName: "total_scheduled_job_filters",
    totalActiveFilters,
  })

  const setSearchValue = useCallback(
    (value: string) => {
      setField("searchValue", value)
      if (value.trim()) {
        trackEvent(TRACKING_EVENTS.RUNS_FILTER.SEARCH_APPLIED, {
          search_length: value.length,
          total_active_filters: totalActiveFilters + 1,
        })
      }
    },
    [trackEvent, setField, totalActiveFilters],
  )

  const setDateRange = useCallback(
    (dateRange: FilterState["dateRange"]) => {
      setField("dateRange", dateRange)
      trackEvent(TRACKING_EVENTS.RUNS_FILTER.DATE_RANGE_APPLIED, {
        has_start_date: Boolean(dateRange.start),
        has_end_date: Boolean(dateRange.end),
        total_active_filters: totalActiveFilters,
      })
    },
    [trackEvent, setField, totalActiveFilters],
  )

  const {
    add: addStatusFilter,
    remove: removeStatusFilter,
    set: setStatusFilters,
  } = statusActions
  const {
    add: addTemplateFilter,
    remove: removeTemplateFilter,
    set: setTemplateFilters,
  } = templateActions
  const {
    add: addPersonaFilter,
    remove: removePersonaFilter,
    set: setPersonaFilters,
  } = personaActions
  const {
    add: addAgentFilter,
    remove: removeAgentFilter,
    set: setAgentFilters,
  } = agentActions
  const {
    add: addMetricFilter,
    remove: removeMetricFilter,
    set: setMetricFilters,
  } = metricActions

  const addMetricValueFilter = useCallback(
    (filter: MetricValueFilter) => {
      if (
        state.metricValueFilters.some((existing) =>
          metricValueFilterEquals(existing, filter),
        )
      ) {
        return
      }

      updateMetricValues((prev) => {
        if (
          prev.some((existing) => metricValueFilterEquals(existing, filter))
        ) {
          return prev
        }

        return [...prev, filter]
      })

      trackEvent(TRACKING_EVENTS.RUNS_FILTER.METRIC_VALUE_ADDED, {
        filter_type: "metricValue",
        filter,
        total_metric_value_filters: state.metricValueFilters.length + 1,
        total_active_filters: totalActiveFilters + 1,
      })
    },
    [
      trackEvent,
      state.metricValueFilters,
      totalActiveFilters,
      updateMetricValues,
    ],
  )

  const removeMetricValueFilter = useCallback(
    (filter: MetricValueFilter) => {
      if (
        !state.metricValueFilters.some((existing) =>
          metricValueFilterEquals(existing, filter),
        )
      ) {
        return
      }

      updateMetricValues((prev) =>
        prev.filter((existing) => !metricValueFilterEquals(existing, filter)),
      )
      trackEvent(TRACKING_EVENTS.RUNS_FILTER.METRIC_VALUE_REMOVED, {
        filter_type: "metricValue",
        filter,
        total_metric_value_filters: Math.max(
          state.metricValueFilters.length - 1,
          0,
        ),
        total_active_filters: Math.max(totalActiveFilters - 1, 0),
      })
    },
    [
      trackEvent,
      state.metricValueFilters,
      totalActiveFilters,
      updateMetricValues,
    ],
  )

  const setMetricValueFilters = useCallback(
    (filters: MetricValueFilter[]) => {
      setField("metricValueFilters", filters)
      trackEvent(TRACKING_EVENTS.RUNS_FILTER.METRIC_VALUE_BULK_UPDATED, {
        filter_type: "metricValue",
        filters,
        filter_count: filters.length,
        total_metric_value_filters: filters.length,
        total_active_filters:
          totalActiveFilters - state.metricValueFilters.length + filters.length,
      })
    },
    [trackEvent, setField, state.metricValueFilters.length, totalActiveFilters],
  )

  const {
    add: addTagFilter,
    remove: removeTagFilter,
    set: setTagFilters,
  } = tagActions
  const {
    add: addMutationFilter,
    remove: removeMutationFilter,
    set: setMutationFilters,
  } = mutationActions
  const {
    add: addTestSetFilter,
    remove: removeTestSetFilter,
    set: setTestSetFilters,
  } = testSetActions
  const {
    add: addTestCaseFilter,
    remove: removeTestCaseFilter,
    set: setTestCaseFilters,
  } = testCaseActions
  const {
    add: addScheduledJobFilter,
    remove: removeScheduledJobFilter,
    set: setScheduledJobFilters,
  } = scheduledJobActions

  const setSortDescriptor = useCallback(
    (descriptor: RunsSortDescriptor) => {
      setField("sortDescriptor", descriptor)
      trackEvent(TRACKING_EVENTS.RUNS_FILTER.SORT_CHANGED, {
        column: descriptor.column,
        direction: descriptor.direction,
        total_active_filters: totalActiveFilters,
      })
    },
    [trackEvent, setField, totalActiveFilters],
  )

  const clearAllFilters = useCallback(() => {
    resetFilters(state.sortDescriptor)
    trackEvent(TRACKING_EVENTS.RUNS_FILTER.CLEAR_ALL, {
      previous_active_filters: totalActiveFilters,
    })
  }, [trackEvent, resetFilters, state.sortDescriptor, totalActiveFilters])

  return useMemo(
    () => ({
      totalActiveFilters,
      hasActiveFilters,
      setSearchValue,
      setDateRange,
      addStatusFilter,
      removeStatusFilter,
      setStatusFilters,
      addTemplateFilter,
      removeTemplateFilter,
      setTemplateFilters,
      addPersonaFilter,
      removePersonaFilter,
      setPersonaFilters,
      addAgentFilter,
      removeAgentFilter,
      setAgentFilters,
      addMetricFilter,
      removeMetricFilter,
      setMetricFilters,
      addMetricValueFilter,
      removeMetricValueFilter,
      setMetricValueFilters,
      addTagFilter,
      removeTagFilter,
      setTagFilters,
      addMutationFilter,
      removeMutationFilter,
      setMutationFilters,
      addTestSetFilter,
      removeTestSetFilter,
      setTestSetFilters,
      addTestCaseFilter,
      removeTestCaseFilter,
      setTestCaseFilters,
      addScheduledJobFilter,
      removeScheduledJobFilter,
      setMetadataFilters,
      setScheduledJobFilters,
      setSortDescriptor,
      clearAllFilters,
    }),
    [
      addAgentFilter,
      addMetricFilter,
      addMetricValueFilter,
      addMutationFilter,
      addPersonaFilter,
      addScheduledJobFilter,
      addStatusFilter,
      addTagFilter,
      addTemplateFilter,
      addTestCaseFilter,
      addTestSetFilter,
      clearAllFilters,
      hasActiveFilters,
      removeAgentFilter,
      removeMetricFilter,
      removeMetricValueFilter,
      removeMutationFilter,
      removePersonaFilter,
      removeScheduledJobFilter,
      removeStatusFilter,
      removeTagFilter,
      removeTemplateFilter,
      removeTestCaseFilter,
      removeTestSetFilter,
      setAgentFilters,
      setDateRange,
      setMetricFilters,
      setMetricValueFilters,
      setMutationFilters,
      setPersonaFilters,
      setMetadataFilters,
      setScheduledJobFilters,
      setSearchValue,
      setSortDescriptor,
      setStatusFilters,
      setTagFilters,
      setTemplateFilters,
      setTestCaseFilters,
      setTestSetFilters,
      totalActiveFilters,
    ],
  )
}
